import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, FileText } from "lucide-react";
import { cn } from "@/lib/cn";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import type { SummarySection } from "@/hooks/useSummariesManifest";

export type SummaryPanelProps = {
  open: boolean;
  onClose: () => void;
  section: SummarySection | null;
};

/**
 * Slide-out right-side panel that renders a section summary as markdown
 * with embedded TeX. Used by the textbook viewer; opens when the user
 * taps a section in the sidebar. On mobile it slides up from the bottom
 * (full-screen-ish); on desktop it slides in from the right (480px).
 *
 * Math pipeline -- the only subtle bit:
 *   1. Replace `$$...$$` (block) and `$...$` (inline) with opaque text
 *      placeholders BEFORE running marked. Tokens survive markdown
 *      parsing AND DOMPurify because they are just plain text.
 *   2. Run marked on the placeholdered source.
 *   3. Sanitise with DOMPurify.
 *   4. String-replace the tokens with KaTeX-rendered HTML. KaTeX's
 *      renderToString output is safe to inject; KaTeX has its own
 *      escape semantics and we set `throwOnError: false` so malformed
 *      TeX renders as a visible error instead of crashing the panel.
 */
export function SummaryPanel({ open, onClose, section }: SummaryPanelProps) {
  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const html = useMemo(() => {
    if (!section?.body_markdown) return null;
    // Textbook summaries author intentional paragraph spacing -- soft
    // breaks should NOT become <br>, otherwise a list item with a
    // following $$..$$ display gets an awkward extra line break.
    return renderMarkdownWithMath(section.body_markdown, { breaks: false });
  }, [section?.body_markdown]);

  return (
    <AnimatePresence>
      {open && section ? (
        <>
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
          />
          <motion.aside
            key="panel"
            role="dialog"
            aria-label={section.title}
            className={cn(
              "fixed z-50 bg-surface border-border shadow-2xl flex flex-col",
              // Mobile: bottom sheet, ~88vh, rounded top
              "inset-x-0 bottom-0 max-h-[88vh] rounded-t-2xl border-t",
              // Desktop: right rail, full height, 480px
              "sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:w-[480px] sm:rounded-none sm:rounded-l-2xl sm:border-t-0 sm:border-l",
            )}
            initial={{ y: "100%", x: 0 }}
            animate={{ y: 0, x: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
          >
            <PanelHeader title={section.title} onClose={onClose} />
            <PanelBody html={html} hasBody={!!section.body_markdown} />
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function PanelHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-border bg-surface">
      <span className="grid place-items-center h-8 w-8 rounded-md bg-brand-soft text-brand flex-shrink-0">
        <FileText className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.16em] text-text-subtle">
          Section summary
        </p>
        <h2 className="font-display text-base sm:text-lg font-semibold tracking-tight leading-snug mt-0.5">
          {title}
        </h2>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="p-1.5 -m-1 rounded-md text-text-muted hover:text-text hover:bg-surface-2 transition-colors"
        aria-label="Close summary"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function PanelBody({ html, hasBody }: { html: string | null; hasBody: boolean }) {
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Scroll back to top when the section changes.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [html]);

  if (!hasBody) {
    return (
      <div
        ref={bodyRef}
        className="flex-1 overflow-y-auto px-5 py-10 text-center text-text-muted"
      >
        <p className="text-sm font-medium text-text">Summary not yet available</p>
        <p className="text-xs mt-1 max-w-xs mx-auto">
          We're polishing the written summary for this section. Until then,
          jump to the relevant page in the textbook on the right.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={bodyRef}
      className={cn(
        "flex-1 overflow-y-auto px-5 sm:px-6 py-5",
        // Prose styling -- bespoke since we're not using @tailwindcss/typography
        "text-[15px] leading-[1.65] text-text",
        "[&_h1]:font-display [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:mt-6 [&_h1]:mb-3",
        "[&_h2]:font-display [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:mt-5 [&_h2]:mb-2",
        "[&_h3]:font-display [&_h3]:text-base [&_h3]:font-semibold [&_h3]:tracking-tight [&_h3]:mt-5 [&_h3]:mb-2",
        "[&_h4]:font-medium [&_h4]:text-[15px] [&_h4]:text-text [&_h4]:mt-4 [&_h4]:mb-1.5",
        "[&_h5]:font-medium [&_h5]:text-sm [&_h5]:text-text-muted [&_h5]:uppercase [&_h5]:tracking-[0.08em] [&_h5]:mt-4 [&_h5]:mb-1",
        "[&_h6]:font-medium [&_h6]:text-xs [&_h6]:text-text-subtle [&_h6]:uppercase [&_h6]:tracking-[0.12em] [&_h6]:mt-3 [&_h6]:mb-1",
        "[&_p]:my-3 [&_p]:text-text",
        "[&_strong]:font-semibold [&_strong]:text-text",
        "[&_em]:italic",
        "[&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:marker:text-text-subtle",
        "[&_ol]:my-3 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:marker:text-text-subtle",
        "[&_li]:my-1",
        "[&_code]:font-mono [&_code]:text-[0.85em] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:bg-surface-2 [&_code]:text-text",
        "[&_pre]:my-4 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:bg-surface-2 [&_pre]:overflow-x-auto",
        "[&_pre_code]:p-0 [&_pre_code]:bg-transparent",
        "[&_blockquote]:my-4 [&_blockquote]:pl-4 [&_blockquote]:border-l-2 [&_blockquote]:border-brand [&_blockquote]:text-text-muted [&_blockquote]:italic",
        "[&_a]:text-brand [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:no-underline",
        "[&_hr]:my-6 [&_hr]:border-border",
        "[&_.katex-display]:my-4 [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden",
      )}
      dangerouslySetInnerHTML={{ __html: html ?? "" }}
    />
  );
}

